import * as _ from 'lodash-es';
import { context, type Context, type Span, SpanKind, SpanStatusCode, trace } from '@opentelemetry/api';
import type { Router, RouteLocationNormalized } from 'vue-router';
import { pageLoadDuration } from './metrics';
import { serviceVersion } from './version';

const tracer = trace.getTracer('vue-router', serviceVersion);

let activePageSpan: Span | null = null;
let activePageContext: Context | null = null;
let navigationStartTime: number | null = null;
let activePageRoute: string | null = null;
let pageLoaded = false;
let inflightCount = 0;

/**
 * End the current active span (if any) with OK status.
 */
const endCurrentSpan = (): void => {
  if (activePageSpan) {
    activePageSpan.setStatus({ code: SpanStatusCode.OK });
    activePageSpan.end();
    activePageSpan = null;
  }
  inflightCount = 0;
  pageLoaded = false;
};

/**
 * Try to end the page span if the page has loaded and all HTTP spans have completed.
 */
const tryEndPageSpan = (): void => {
  if (pageLoaded && inflightCount <= 0 && activePageSpan) {
    activePageSpan.setStatus({ code: SpanStatusCode.OK });
    activePageSpan.end();
    activePageSpan = null;
  }
};

/**
 * Called by the HTTP interceptor when an HTTP span starts.
 * Increments the in-flight reference count to keep the page span alive, and
 * returns the span the request is associated with as a release token. Returns
 * null when no page span is active (e.g. API calls before the first navigation),
 * in which case the paired releasePageRef is a no-op.
 */
export const acquirePageRef = (): Span | null => {
  if (!activePageSpan) return null;
  inflightCount++;
  return activePageSpan;
};

/**
 * Called by the HTTP interceptor when an HTTP span ends. Pass the token returned
 * by the paired acquirePageRef.
 *
 * The count is only decremented when the request was acquired against the page
 * span that is still active. A request that outlives its navigation (token !==
 * activePageSpan) is ignored: its page span was already force-ended by the
 * navigation, so decrementing here would corrupt the new page's count and could
 * end its span while it still has genuine requests in flight.
 */
export const releasePageRef = (token: Span | null | undefined): void => {
  if (!token || token !== activePageSpan) return;
  inflightCount = Math.max(0, inflightCount - 1);
  tryEndPageSpan();
};

/**
 * Start a new page span for a route transition.
 * Creates a span immediately so HTTP calls during page load have a parent context.
 */
export const startPageSpan = (route: RouteLocationNormalized): void => {
  endCurrentSpan();
  activePageContext = null;
  navigationStartTime = performance.now();
  pageLoaded = false;
  inflightCount = 0;

  const pageName = buildPageName(route);

  const attributes: Record<string, string> = {
    'page.route': route.name?.toString() || route.path,
    'page.path': route.fullPath,
    'page.url': window.location.href
  };

  // Add route params as attributes
  if (!_.isEmpty(route.params)) {
    _.forEach(route.params, (value, key) => {
      if (!_.isNil(value)) {
        attributes[`page.param.${key}`] = _.toString(value);
      }
    });
  }

  // Add query params as attributes
  if (!_.isEmpty(route.query)) {
    _.forEach(route.query, (value, key) => {
      if (!_.isNil(value)) {
        attributes[`page.query.${key}`] = _.toString(value);
      }
    });
  }

  activePageRoute = route.name?.toString() || route.path;
  activePageSpan = tracer.startSpan(`PAGE ${pageName}`, {
    kind: SpanKind.INTERNAL,
    attributes
  });
  activePageContext = trace.setSpan(context.active(), activePageSpan);
};

/**
 * Build a page name from the route.
 * Format: "/path PageName" (e.g., "/clients Clients", "/process Process")
 */
const buildPageName = (route: RouteLocationNormalized): string => {
  const path = route.path || '/';
  const name = route.name?.toString() || route.meta?.title?.toString() || '';

  // Derive title from route name if available
  let title = name;
  if (name) {
    // Convert route name from camelCase or kebab-case to Title Case
    title = _.map(_.split(name, /[-_]/), (word) => _.upperFirst(word)).join(' ');
  } else if (path !== '/') {
    // Fallback: derive from path
    const segments = _.filter(_.split(path, '/'), Boolean);
    if (!_.isEmpty(segments)) {
      const last = _.last(segments)!;
      title = _.upperFirst(last);
    }
  }

  // Format: /path Title (only include path if it's not just root "/")
  return path !== '/' && title ? `${path} ${title}` : title || path;
};

/**
 * Mark the current page as loaded.
 * Records the page.loaded event and load duration metric.
 * The page span ends when all in-flight HTTP spans complete (via releasePageRef).
 * If no HTTP spans are in-flight, the span ends immediately.
 */
export const markPageLoaded = (): void => {
  if (activePageSpan) {
    activePageSpan.addEvent('page.loaded');

    if (navigationStartTime !== null) {
      const duration = performance.now() - navigationStartTime;
      pageLoadDuration?.record(duration, {
        'page.route': activePageRoute || 'unknown'
      });
      navigationStartTime = null;
    }
  }

  pageLoaded = true;
  tryEndPageSpan();
};

/**
 * End the current page span with an optional error.
 */
export const endPageSpan = (error?: Error): void => {
  if (activePageSpan) {
    if (error) {
      activePageSpan.setStatus({ code: SpanStatusCode.ERROR, message: error.message });
      activePageSpan.recordException(error);
    } else {
      activePageSpan.setStatus({ code: SpanStatusCode.OK });
    }
    activePageSpan.end();
    activePageSpan = null;
  }
  activePageContext = null;
  inflightCount = 0;
  pageLoaded = false;
};

/**
 * Get the current page span's context.
 * Used by the HTTP interceptor to parent HTTP spans under the page span.
 */
export const getPageContext = (): Context => {
  return activePageContext || context.active();
};

/**
 * Name of the currently active page route (the last route navigated to), or undefined
 * before the first navigation. Used to enrich bridge logs with a page name when the
 * message itself doesn't carry one (e.g. save/reloadClientMenu, and all inbound events
 * other than loadPage/loadTab).
 */
export const getActivePageRoute = (): string | undefined => activePageRoute ?? undefined;

/**
 * Run a callback within the active page span's context. Anything created inside —
 * notably logs (the logs SDK stamps trace_id/span_id onto the LogRecord from the
 * active context) — then correlates to the PAGE span and its trace. Falls back to
 * the active context when no page span is active (e.g. init/login before first
 * navigation), attaching no trace context.
 */
export const runInPageContext = <T>(fn: () => T): T => context.with(getPageContext(), fn);

// Safety net: end any active span when the page unloads (tab close, browser close, full-page navigation).
// pagehide is primary (works on mobile, bfcache-safe); beforeunload is a fallback.
if (typeof window !== 'undefined') {
  const cleanup = () => endCurrentSpan();
  window.addEventListener('pagehide', cleanup);
  window.addEventListener('beforeunload', cleanup);
}

/**
 * Install the router tracking plugin.
 * Call this with your Vue Router instance to enable automatic page span tracking.
 */
export const installRouterTracking = (router: Router): void => {
  // No exporter endpoint configured → telemetry is disabled, so don't wire up
  // any router navigation hooks.
  if (!import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT) {
    console.warn('[vue:telemetry] otelExporterEndpoint not set — Router tracking disabled');
    return;
  }

  console.log('[vue:telemetry] Router tracking initialized');

  // Start a page span when navigation begins
  router.beforeEach((to) => {
    console.log('[vue:telemetry] Page transition start:', to.path);
    startPageSpan(to);
  });

  // Mark the page as loaded after navigation completes successfully
  router.afterEach((to) => {
    console.log('[vue:telemetry] Page transition success:', to.path);
    markPageLoaded();
  });

  // End the page span with an error if navigation fails
  router.onError((error) => {
    console.error('[vue:telemetry] Page transition error:', error);
    endPageSpan(error);
  });
};
